import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Switch,
  Modal,
  message,
  Statistic,
  Row,
  Col,
  Typography,
  Form,
  Input,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  FileTextOutlined, 
  SettingOutlined,
  CrownOutlined,
  ReloadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import api from '../services/api';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { TabPane } = Tabs;

interface User {
  id: number;
  name: string;
  wechat_userid: string;
  department?: string;
  email?: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
}

interface AdminTicket {
  id: number;
  dms_ticket_code: string;
  title: string;
  status: string;
  priority: string;
  submitter_name: string;
  assignee_name?: string;
  created_at: string;
  updated_at: string;
}

interface AdminStats {
  total_users: number;
  total_admins: number;
  total_tickets: number;
  ticket_stats: {
    pending: number;
    approved: number;
    rejected: number;
    executed: number;
  };
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [tickets, setTickets] = useState<AdminTicket[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [addUserForm] = Form.useForm();

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'tickets') {
      loadTickets();
    } else if (activeTab === 'stats') {
      loadStats();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      message.error('加载用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/tickets');
      setTickets(response.data);
    } catch (error) {
      message.error('加载工单列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      message.error('加载统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSetAdmin = async (userId: number, isAdmin: boolean, userName: string) => {
    try {
      await api.post(`/admin/users/${userId}/set-admin`, null, {
        params: { is_admin: isAdmin }
      });
      
      message.success(
        isAdmin ? `${userName} 已设置为管理员` : `${userName} 的管理员权限已移除`
      );
      
      // 重新加载用户列表
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '操作失败');
    }
  };

  const handleReassignTicket = (ticketId: number, ticketCode: string) => {
    Modal.info({
      title: `重新分配工单 ${ticketCode}`,
      content: '此功能需要选择新的审批人，暂未实现完整的用户选择界面。',
      onOk: () => {
        // TODO: 实现重新分配功能
      }
    });
  };

  const handleAddUser = async (values: any) => {
    try {
      await api.post('/admin/users/manual-create', values);
      message.success(`用户 ${values.name} 添加成功`);
      setAddUserModalVisible(false);
      addUserForm.resetFields();
      loadUsers(); // 重新加载用户列表
    } catch (error: any) {
      message.error(error.response?.data?.detail || '添加用户失败');
    }
  };

  const handleDeleteUser = (userId: number, userName: string) => {
    Modal.confirm({
      title: '确认删除用户',
      content: `确定要删除用户 "${userName}" 吗？此操作不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/admin/users/${userId}`);
          message.success(`用户 ${userName} 删除成功`);
          loadUsers(); // 重新加载用户列表
        } catch (error: any) {
          message.error(error.response?.data?.detail || '删除用户失败');
        }
      }
    });
  };

  const handleDeleteTicket = (ticketId: number, ticketCode: string) => {
    Modal.confirm({
      title: '确认删除工单',
      content: `确定要删除工单 "${ticketCode}" 吗？此操作不可恢复。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/admin/tickets/${ticketId}`);
          message.success(`工单 ${ticketCode} 删除成功`);
          loadTickets(); // 重新加载工单列表
        } catch (error: any) {
          message.error(error.response?.data?.detail || '删除工单失败');
        }
      }
    });
  };

  const showAddUserModal = () => {
    setAddUserModalVisible(true);
  };

  const hideAddUserModal = () => {
    setAddUserModalVisible(false);
    addUserForm.resetFields();
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      executed: 'blue',
      failed: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts = {
      pending: '待审批',
      approved: '已通过',
      rejected: '已拒绝',
      executed: '已执行',
      failed: '执行失败'
    };
    return texts[status as keyof typeof texts] || status;
  };

  const userColumns: ColumnsType<User> = [
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '企微ID',
      dataIndex: 'wechat_userid',
      key: 'wechat_userid',
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      render: (dept) => dept || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '-',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '管理员',
      key: 'admin',
      render: (_, record) => (
        <Switch
          checked={record.is_admin}
          onChange={(checked) => handleSetAdmin(record.id, checked, record.name)}
          checkedChildren="是"
          unCheckedChildren="否"
        />
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          danger
          size="small"
          onClick={() => handleDeleteUser(record.id, record.name)}
        >
          删除
        </Button>
      ),
    },
  ];

  const ticketColumns: ColumnsType<AdminTicket> = [
    {
      title: '工单编号',
      dataIndex: 'dms_ticket_code',
      key: 'dms_ticket_code',
      render: (code) => (
        <Tag color="blue" style={{ fontFamily: 'monospace' }}>
          {code}
        </Tag>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '提交人',
      dataIndex: 'submitter_name',
      key: 'submitter_name',
    },
    {
      title: '审批人',
      dataIndex: 'assignee_name',
      key: 'assignee_name',
      render: (name) => name || '未分配',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            onClick={() => handleReassignTicket(record.id, record.dms_ticket_code)}
          >
            重新分配
          </Button>
          <Button
            type="link"
            danger
            size="small"
            onClick={() => handleDeleteTicket(record.id, record.dms_ticket_code)}
          >
            删除
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card>
        <Title level={3}>
          <SettingOutlined /> 管理面板
        </Title>
        
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="统计概览" key="stats">
            {stats && (
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="总用户数"
                      value={stats.total_users}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="管理员数"
                      value={stats.total_admins}
                      prefix={<CrownOutlined />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="总工单数"
                      value={stats.total_tickets}
                      prefix={<FileTextOutlined />}
                      valueStyle={{ color: '#13c2c2' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="待审批"
                      value={stats.ticket_stats.pending}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="已通过"
                      value={stats.ticket_stats.approved}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="已拒绝"
                      value={stats.ticket_stats.rejected}
                      valueStyle={{ color: '#f5222d' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="已执行"
                      value={stats.ticket_stats.executed}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>

          <TabPane tab="用户管理" key="users">
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button 
                  type="primary"
                  icon={<PlusOutlined />} 
                  onClick={showAddUserModal}
                >
                  添加用户
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadUsers}
                  loading={loading}
                >
                  刷新
                </Button>
              </Space>
            </div>
            <Table
              columns={userColumns}
              dataSource={users}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              }}
            />
          </TabPane>

          <TabPane tab="工单管理" key="tickets">
            <div style={{ marginBottom: 16 }}>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadTickets}
                loading={loading}
              >
                刷新
              </Button>
            </div>
            <Table
              columns={ticketColumns}
              dataSource={tickets}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 添加用户Modal */}
      <Modal
        title="添加用户"
        open={addUserModalVisible}
        onCancel={hideAddUserModal}
        footer={null}
        width={600}
      >
        <Form
          form={addUserForm}
          layout="vertical"
          onFinish={handleAddUser}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="姓名"
                name="name"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入用户姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="企微用户ID"
                name="wechat_userid"
                rules={[{ required: true, message: '请输入企微用户ID' }]}
              >
                <Input placeholder="请输入企微用户ID" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="DMS UID"
                name="dms_uid"
              >
                <Input placeholder="请输入DMS用户ID（可选）" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="部门"
                name="department"
              >
                <Input placeholder="请输入部门名称（可选）" />
              </Form.Item>
            </Col>
          </Row>
          
                     <Row gutter={16}>
             <Col span={12}>
               <Form.Item
                 label="邮箱"
                 name="email"
                 rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
               >
                 <Input placeholder="请输入邮箱地址（可选）" />
               </Form.Item>
             </Col>
             <Col span={12}>
               <Form.Item
                 label="头像URL"
                 name="avatar_url"
               >
                 <Input placeholder="请输入头像URL（可选）" />
               </Form.Item>
             </Col>
           </Row>

          <Divider />
          
          <div style={{ textAlign: 'center' }}>
            <Space>
              <Button onClick={hideAddUserModal}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                添加用户
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPanel;

